// Window snap logic - extracted for testing
// Grid geometry: single source is ./grid — re-exported for existing importers.
export {
  FLOOR_WIDTH,
  FLOOR_HEIGHT,
  BASE_MAP_WIDTH_TILES,
  GRID_SUBDIVISION,
  MAP_WIDTH_TILES,
  TILE_WIDTH,
  TILE_HEIGHT,
  HALF_TILE_WIDTH,
  HALF_TILE_HEIGHT,
} from './grid';
import {
  MAP_WIDTH_TILES,
  TILE_WIDTH,
  TILE_HEIGHT,
  HALF_TILE_WIDTH,
  HALF_TILE_HEIGHT,
} from './grid';

export const WINDOW_VERTICAL_OFFSET = -80;
export const WINDOW_WALL_SNAP_TILES = 6; // same physical distance as old 3 at half tile size

const halfTileWidth = HALF_TILE_WIDTH;
const halfTileHeight = HALF_TILE_HEIGHT;

export interface WindowSnapResult {
  x: number;
  y: number;
  shouldMirror: boolean;
  isOnWall: boolean;
  baseTileX: number;
  baseTileY: number;
}

export function snapWindowAnchorToGrid(
  anchorX: number,
  anchorY: number,
  rawEffectiveWidthTiles: number,
  rawEffectiveHeightTiles: number,
  cursorWorldX?: number,  // Optional: raw cursor X for wall selection during drag
  previousSnapResult?: WindowSnapResult,  // Optional: previous snap for distance preservation on transition
  // Drag mode: the item stays ON the wall but slides along it smoothly instead of
  // stepping tile by tile. baseTileX/Y stay whole tiles (the rounded position) so
  // every validity check reads the same tile the drop will save.
  continuous = false,
): WindowSnapResult {
  // 1. Rough tile coords from raw anchor position
  //    CRITICAL: Use anchorY directly (without subtracting WINDOW_VERTICAL_OFFSET)
  //    to match getFurnitureDepthComponents logic, which uses marker.position.y
  //    directly for tile calculation. The offset is only for visual positioning,
  //    not for tile coordinate calculation.
  const rawTileX = (anchorX / halfTileWidth + anchorY / halfTileHeight) / 2;
  const rawTileY = (anchorY / halfTileHeight - anchorX / halfTileWidth) / 2;
  const frontX = Math.round(rawTileX + 1e-6);
  const frontY = Math.round(rawTileY + 1e-6);

  // 2. Figure out nearest wall by checking baseTile distance in EACH mirror state
  //    Left wall: mirrored → effW = rawEffH (thin side perpendicular to wall)
  //    Right wall: not mirrored → effH = rawEffH (thin side perpendicular to wall)
  const baseTileX_ifMirrored = frontX - rawEffectiveHeightTiles;  // mirrored swaps W↔H
  const baseTileY_ifNotMirrored = frontY - rawEffectiveHeightTiles;
  const distToLeft = Math.max(0, baseTileX_ifMirrored);
  const distToRight = Math.max(0, baseTileY_ifNotMirrored);

  let nearestWall: 'left' | 'right';
  // Use cursor/hysteresis only when walls are genuinely ambiguous (similar distances).
  // The old CORNER_TRANSITION_ZONE approach was too aggressive: for a 7-tile-wide painting,
  // distToLeft=0, distToRight=7 both < 9 → entered "ambiguous" zone even though the item
  // was clearly on the left wall. This caused jumps to the other wall when cursor went
  // slightly positive while dragging near the corner.
  // Now: only use cursor when |distToLeft - distToRight| < AMBIGUOUS_THRESHOLD (3 tiles).
  const AMBIGUOUS_THRESHOLD = 3;
  if (Math.abs(distToLeft - distToRight) < AMBIGUOUS_THRESHOLD) {
    // Near-equal distances: walls are genuinely ambiguous — use cursor/hysteresis.
    if (cursorWorldX !== undefined) {
      if (previousSnapResult) {
        // HYSTERESIS: stay on current wall unless cursor clearly crossed to the other side.
        const STICKY_MARGIN = TILE_WIDTH * 3; // 75 px — cursor must cross 3 tiles past center
        if (previousSnapResult.shouldMirror) {
          // Currently on left wall — only switch to right if cursor is clearly positive.
          nearestWall = cursorWorldX > STICKY_MARGIN ? 'right' : 'left';
        } else {
          // Currently on right wall — only switch to left if cursor is clearly negative.
          nearestWall = cursorWorldX < -STICKY_MARGIN ? 'left' : 'right';
        }
      } else {
        // No previous snap — use raw cursor position (first placement).
        nearestWall = cursorWorldX > 0 ? 'right' : 'left';
      }
    } else {
      // Non-drag: use anchor position.
      nearestWall = rawTileX > rawTileY ? 'right' : 'left';
    }
  } else {
    // Clear winner — pick the closer wall directly, no cursor bias needed.
    nearestWall = distToLeft < distToRight ? 'left' : 'right';
  }
  const shouldMirror = nearestWall === 'left';

  // 3. Compute snap with correct dimensions for the target mirror state
  const effW = shouldMirror ? rawEffectiveHeightTiles : rawEffectiveWidthTiles;
  const effH = shouldMirror ? rawEffectiveWidthTiles  : rawEffectiveHeightTiles;
  const maxBaseTileX = MAP_WIDTH_TILES - effW;
  const maxBaseTileY = MAP_WIDTH_TILES - effH;

  let baseTileX = frontX - effW;
  let baseTileY = frontY - effH;

  // 4. CRITICAL: Always snap to nearest wall (windows can ONLY be placed on walls)
  //
  // CORNER TRANSITION: When previousSnapResult is provided and the wall changed,
  // PRESERVE the distance from the corner. E.g., baseTileY=4 on left wall →
  // baseTileX=4 on right wall. This ensures a smooth visual transition.
  const isTransition = previousSnapResult && (shouldMirror !== previousSnapResult.shouldMirror);

  if (isTransition) {
    // Wall transition detected! Preserve distance from corner.
    if (nearestWall === 'right' && previousSnapResult.shouldMirror) {
      // Was on left wall (baseTileX=0, baseTileY=N) → now on right wall (baseTileX=N, baseTileY=0)
      baseTileX = Math.max(1, previousSnapResult.baseTileY);
      baseTileY = 0;
    } else if (nearestWall === 'left' && !previousSnapResult.shouldMirror) {
      // Was on right wall (baseTileX=N, baseTileY=0) → now on left wall (baseTileX=0, baseTileY=N)
      baseTileX = 0;
      baseTileY = Math.max(1, previousSnapResult.baseTileX);
    }
  } else {
    // Normal snap (no transition)
    if (nearestWall === 'left') {
      baseTileX = 0;
    }
    if (nearestWall === 'right') {
      baseTileY = 0;
    }
  }

  // 5. Clamp to valid grid range
  baseTileX = Math.max(0, Math.min(maxBaseTileX, baseTileX));
  baseTileY = Math.max(0, Math.min(maxBaseTileY, baseTileY));

  const isOnWall = (baseTileX === 0 || baseTileY === 0);

  // 5b. Smooth slide along the wall (drag): the along-wall coordinate comes from
  //     the horizontal position ONLY (the vertical one is the item's height on the
  //     wall — see clampWallFloorOffset), unrounded and clamped to the wall's length;
  //     the wall coordinate stays 0.
  let posTileX = baseTileX;
  let posTileY = baseTileY;
  if (continuous && !isTransition) {
    if (nearestWall === 'right') {
      // On the right wall frontY = effH, so x = (frontX − effH)·halfW.
      posTileX = Math.max(0, Math.min(maxBaseTileX, anchorX / halfTileWidth + effH - effW));
      baseTileX = Math.round(posTileX);
    } else {
      // On the left wall frontX = effW, so x = (effW − frontY)·halfW.
      posTileY = Math.max(0, Math.min(maxBaseTileY, effW - anchorX / halfTileWidth - effH));
      baseTileY = Math.round(posTileY);
    }
  }

  // 6. Convert back to world coords
  const snappedFrontX = posTileX + effW;
  const snappedFrontY = posTileY + effH;
  return {
    x: (snappedFrontX - snappedFrontY) * halfTileWidth,
    y: (snappedFrontX + snappedFrontY) * halfTileHeight + WINDOW_VERTICAL_OFFSET,
    shouldMirror,
    isOnWall,
    baseTileX,
    baseTileY,
  };
}

// Align window position to start of wall when transitioning to another wall
// This prevents window from getting stuck in the middle when transitioning
export function alignWindowToWallStart(
  snapResult: WindowSnapResult,
  previousMirror: boolean,
  _previousX: number,
  _previousY: number,
  rawEffectiveWidthTiles: number,
  rawEffectiveHeightTiles: number,
): WindowSnapResult {
  // If mirror state changed, window transitioned to another wall
  if (snapResult.shouldMirror !== previousMirror) {
    // Use snapResult's coordinates directly - they are already correct for the new wall
    // Just ensure we keep away from corner
    if (snapResult.shouldMirror) {
      // Now on left wall - ensure baseTileX=0 and keep away from corner
      if (snapResult.baseTileY < 1) {
        // Too close to corner, adjust baseTileY
        const effW = rawEffectiveHeightTiles;
        const effH = rawEffectiveWidthTiles;
        const baseTileX = 0;
        const baseTileY = 1;
        const snappedFrontX = baseTileX + effW;
        const snappedFrontY = baseTileY + effH;
        return {
          x: (snappedFrontX - snappedFrontY) * halfTileWidth,
          y: (snappedFrontX + snappedFrontY) * halfTileHeight + WINDOW_VERTICAL_OFFSET,
          shouldMirror: true,
          isOnWall: true,
          baseTileX,
          baseTileY,
        };
      }
      // Use snapResult directly - it already has correct coordinates
      return snapResult;
    } else {
      // Now on right wall - ensure baseTileY=0 and keep away from corner
      if (snapResult.baseTileX < 1) {
        // Too close to corner, adjust baseTileX
        const effW = rawEffectiveWidthTiles;
        const effH = rawEffectiveHeightTiles;
        const baseTileX = 1;
        const baseTileY = 0;
        const snappedFrontX = baseTileX + effW;
        const snappedFrontY = baseTileY + effH;
        return {
          x: (snappedFrontX - snappedFrontY) * halfTileWidth,
          y: (snappedFrontX + snappedFrontY) * halfTileHeight + WINDOW_VERTICAL_OFFSET,
          shouldMirror: false,
          isOnWall: true,
          baseTileX,
          baseTileY,
        };
      }
      // For right wall, use snapResult's coordinates directly
      // snapResult already has the correct position for right wall
      // Just ensure we keep away from corner
      const effW = rawEffectiveWidthTiles;
      const effH = rawEffectiveHeightTiles;
      const baseTileX = Math.max(1, snapResult.baseTileX); // Keep away from corner
      const baseTileY = 0;
      
      // Recalculate coordinates from baseTileX and baseTileY to ensure they're correct
      const snappedFrontX = baseTileX + effW;
      const snappedFrontY = baseTileY + effH;
      const finalX = (snappedFrontX - snappedFrontY) * halfTileWidth;
      const finalY = (snappedFrontX + snappedFrontY) * halfTileHeight + WINDOW_VERTICAL_OFFSET;
      
      // Return the recalculated position - it should be on right wall
      // Even if snapWindowAnchorToGrid verifies it as left wall due to equal distances,
      // we know it should be on right wall because snapResult.shouldMirror=false
      return {
        x: finalX,
        y: finalY,
        shouldMirror: false,
        isOnWall: true,
        baseTileX,
        baseTileY,
      };
    }
  }
  
  // No transition - return original result
  return snapResult;
}

// Check if window can move further along the wall (prevents going past corner)
export function canWindowMoveFurther(
  currentX: number,
  currentY: number,
  newX: number,
  newY: number,
  rawEffectiveWidthTiles: number,
  rawEffectiveHeightTiles: number,
): boolean {
  const currentSnap = snapWindowAnchorToGrid(currentX, currentY, rawEffectiveWidthTiles, rawEffectiveHeightTiles);
  const newSnap = snapWindowAnchorToGrid(newX, newY, rawEffectiveWidthTiles, rawEffectiveHeightTiles);
  
  // Calculate maximum possible positions
  const maxBaseTileX = MAP_WIDTH_TILES - (currentSnap.shouldMirror ? rawEffectiveHeightTiles : rawEffectiveWidthTiles);
  const maxBaseTileY = MAP_WIDTH_TILES - (currentSnap.shouldMirror ? rawEffectiveWidthTiles : rawEffectiveHeightTiles);
  
  // CRITICAL: If mirror state changes, this means window is transitioning from one wall to another
  // Block this ONLY if current position is EXACTLY at corner (0,0)
  // If window is not at exact corner, allow transition to other wall
  if (currentSnap.shouldMirror !== newSnap.shouldMirror) {
    // Block transition ONLY if at exact corner (0,0)
    // This prevents window from "cutting through" the corner visually
    if (currentSnap.baseTileX === 0 && currentSnap.baseTileY === 0) {
      return false; // Blocked: transitioning through exact corner
    }
    // Otherwise allow transition (window is not at exact corner, can safely transition)
  }
  
  // If current position is at corner (0,0), movement is blocked
  if (currentSnap.baseTileX === 0 && currentSnap.baseTileY === 0) {
    return false;
  }
  
  // If new position would be at corner (0,0), movement is blocked
  if (newSnap.baseTileX === 0 && newSnap.baseTileY === 0) {
    return false;
  }
  
  // If on left wall (baseTileX=0), can only move along Y axis (not towards corner)
  if (currentSnap.baseTileX === 0 && newSnap.baseTileX === 0) {
    // Moving towards corner means baseTileY decreasing towards 0
    // Block if:
    //   1. Moving closer to corner (newSnap.baseTileY < currentSnap.baseTileY), AND
    //   2. Would reach critical distance (newSnap.baseTileY <= 3)
    // This prevents window from getting too close to corner where it would visually overlap
    // Blocks movement from baseTileY=4→3, 3→2, 2→1, but allows movement when baseTileY increases (away from corner)
    if (newSnap.baseTileY < currentSnap.baseTileY && newSnap.baseTileY <= 3) {
      return false; // Blocked: moving towards corner and would reach critical distance
    }
    
    // CRITICAL: If baseTileY doesn't change, block movement to prevent "jumping" on same tile
    // EXCEPTION: If window is already at maximum, allow movement to stay at maximum
    // This allows window to reach and stay at the very end of the wall
    if (newSnap.baseTileY === currentSnap.baseTileY) {
      // Same tile - check if cursor is actually moving
      // If cursor is moving, allow movement even if baseTileY doesn't change
      // This allows smooth movement along wall when cursor moves
      const cursorMovement = Math.sqrt((newX - currentX) ** 2 + (newY - currentY) ** 2);
      const MIN_CURSOR_MOVEMENT = 1; // pixels - minimum cursor movement to allow window movement
      
      if (cursorMovement < MIN_CURSOR_MOVEMENT) {
        // Cursor hasn't moved - block movement to prevent unnecessary updates
        return false; // Blocked: cursor hasn't moved
      }
      
      // Cursor is moving - allow movement even if baseTileY doesn't change
      // This allows smooth movement along wall when cursor moves
      // Window will snap to the same tile, but position can still update slightly
      return true; // Allowed: cursor is moving, allow movement even if on same tile
    }
    
    // Check if trying to move beyond maximum (towards darkness/end of wall)
    if (newSnap.baseTileY > maxBaseTileY) {
      return false;
    }
  }
  
  // If on right wall (baseTileY=0), can only move along X axis (not towards corner)
  if (currentSnap.baseTileY === 0 && newSnap.baseTileY === 0) {
    // Moving towards corner means baseTileX decreasing towards 0
    // Block if:
    //   1. Moving closer to corner (newSnap.baseTileX < currentSnap.baseTileX), AND
    //   2. Would reach critical distance (newSnap.baseTileX <= 3)
    if (newSnap.baseTileX < currentSnap.baseTileX && newSnap.baseTileX <= 3) {
      return false; // Blocked: moving towards corner and would reach critical distance
    }
    
    // CRITICAL: If baseTileX doesn't change, allow movement if cursor is moving
    // EXCEPTION: If window is already at maximum, allow movement to stay at maximum
    // This allows window to reach and stay at the very end of the wall
    if (newSnap.baseTileX === currentSnap.baseTileX) {
      // Same tile - check if cursor is actually moving
      // If cursor is moving, allow movement even if baseTileX doesn't change
      // This allows smooth movement along wall when cursor moves
      const cursorMovement = Math.sqrt((newX - currentX) ** 2 + (newY - currentY) ** 2);
      const MIN_CURSOR_MOVEMENT = 1; // pixels - minimum cursor movement to allow window movement
      
      if (cursorMovement < MIN_CURSOR_MOVEMENT) {
        // Cursor hasn't moved - block movement to prevent unnecessary updates
        return false; // Blocked: cursor hasn't moved
      }
      
      // Cursor is moving - allow movement even if baseTileX doesn't change
      // This allows smooth movement along wall when cursor moves
      // Window will snap to the same tile, but position can still update slightly
      return true; // Allowed: cursor is moving, allow movement even if on same tile
    }
    
    // Check if trying to move beyond maximum (towards darkness/end of wall)
    if (newSnap.baseTileX > maxBaseTileX) {
      return false;
    }
  }
  
  return true;
}

/** Height of the room walls in room-local px: the wall art is 500×550 with its
 *  bottom edge 303 px below the top at the corner (WALL_ANCHOR_Y = 303/550). */
export const WALL_HEIGHT_PX = 303;

/** Rise of a wall's base/top line per room px along x (both walls run at the floor's slope). */
export const WALL_SLOPE = HALF_TILE_HEIGHT / HALF_TILE_WIDTH;

/**
 * Real height of a wall item. Its sprite is drawn slanted along the wall, so the
 * sprite box is taller than the item by the slant (width × slope); the frame's
 * depth adds the same amount to both terms and cancels out.
 */
export function wallItemHeightPx(spriteWidth: number, spriteHeight: number): number {
  return Math.max(0, Math.abs(spriteHeight) - Math.abs(spriteWidth) * WALL_SLOPE);
}

/** Gap between the wall's base line and the bottom of an item hung at wall_floor_offset 0. */
function wallBottomGap(thicknessTiles: number): number {
  return -WINDOW_VERTICAL_OFFSET - thicknessTiles * TILE_HEIGHT;
}

/**
 * Vertical extent of a wall item measured as height above the wall's base line
 * (room px, + = up). Unlike screen y this is the same anywhere along the wall
 * and on both walls, so it is what overlap checks and alignment compare.
 */
export function wallVerticalSpan(
  wallFloorOffset: number,
  thicknessTiles: number,
  itemHeightPx: number,
): { bottom: number; top: number } {
  const bottom = wallBottomGap(thicknessTiles) - wallFloorOffset;
  return { bottom, top: bottom + itemHeightPx };
}

/**
 * Keep a wall item ON its wall vertically: wall_floor_offset (px, + = lower) such
 * that the item's bottom is not below the floor line and its top not above the
 * wall's top edge. The marker sits at floorY + WINDOW_VERTICAL_OFFSET + offset,
 * and floorY is `thicknessTiles` tiles in front of the wall's base line.
 * `itemHeightPx` is the item's real height (wallItemHeightPx), not its sprite box.
 */
export function clampWallFloorOffset(offset: number, itemHeightPx: number, thicknessTiles: number): number {
  const bottomGap = wallBottomGap(thicknessTiles);              // bottom above the wall base at offset 0
  const maxOffset = bottomGap;                                  // bottom on the floor line
  const minOffset = itemHeightPx - (WALL_HEIGHT_PX - bottomGap); // top on the wall's top edge
  if (minOffset > maxOffset) return maxOffset; // taller than the wall: keep it on the floor line
  return Math.max(minOffset, Math.min(maxOffset, offset));
}
